import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { favoriteWorkersAPI } from '../api/api';
import { Button } from '../components/ui/button';
import {
  Star, Trash, SpinnerGap, ArrowLeft, MapPin, Briefcase,
  ChatCircle, Phone, Envelope, PencilSimple, Check, X, UserCircle
} from '@phosphor-icons/react';
import { toast } from '../hooks/use-toast';

const FavoriteWorkers = () => {
  const navigate = useNavigate();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingNote, setEditingNote] = useState(null);
  const [noteValue, setNoteValue] = useState('');

  const fetchFavorites = useCallback(async () => {
    try {
      setLoading(true);
      const res = await favoriteWorkersAPI.list();
      if (res.data?.success) setFavorites(res.data.favorites || []);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userType = localStorage.getItem('userType');
    if (!token) { navigate('/login?type=contractor'); return; }
    if (userType && userType !== 'contractor') { navigate('/contractor-dashboard'); return; }
    fetchFavorites();
  }, [navigate, fetchFavorites]);

  const handleRemove = async (workerId, name) => {
    if (!window.confirm(`Remove ${name} from favorites?`)) return;
    try {
      await favoriteWorkersAPI.remove(workerId);
      toast({ title: `${name} removed from favorites` });
      fetchFavorites();
    } catch { toast({ title: 'Error removing', variant: 'destructive' }); }
  };

  const handleSaveNote = async (workerId) => {
    try {
      await favoriteWorkersAPI.updateNote(workerId, noteValue);
      toast({ title: 'Note saved' });
      setEditingNote(null);
      fetchFavorites();
    } catch { toast({ title: 'Error saving note', variant: 'destructive' }); }
  };

  return (
    <div className="min-h-screen bg-[#F0F0F2] dark:bg-neutral-900 pb-24 transition-colors duration-300" data-testid="favorite-workers-page">
      {/* Header */}
      <div className="bg-neutral-900 dark:bg-black border-b-4 border-[#D95800]">
        <div className="max-w-5xl mx-auto px-4 py-6">
          <button onClick={() => navigate('/contractor-dashboard')} className="flex items-center gap-2 text-neutral-400 hover:text-white mb-4 transition-colors" data-testid="back-to-dashboard">
            <ArrowLeft size={20} weight="bold" /> Back to Dashboard
          </button>
          <h1 className="text-2xl sm:text-3xl font-black text-white flex items-center gap-3">
            <div className="w-10 h-10 bg-[#D95800] rounded-lg flex items-center justify-center border-2 border-black">
              <Star size={22} weight="fill" className="text-white" />
            </div>
            Favorite Workers
          </h1>
          <p className="text-neutral-400 mt-1">Your go-to crew. Star workers from past jobs for instant re-hiring.</p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <SpinnerGap size={48} weight="bold" className="animate-spin text-[#2B4C6F]" />
          </div>
        ) : favorites.length === 0 ? (
          <div className="text-center py-16" data-testid="empty-favorites">
            <Star size={64} weight="bold" className="text-neutral-300 dark:text-neutral-600 mx-auto mb-4" />
            <h3 className="text-xl font-black text-neutral-700 dark:text-neutral-300 mb-2">No Favorites Yet</h3>
            <p className="text-neutral-500 dark:text-neutral-400 mb-6 max-w-md mx-auto">
              Star workers from job applications or the Find Workers page. They'll show up here for quick re-hiring.
            </p>
            <Button onClick={() => navigate('/find-workers')}
              className="h-11 px-6 bg-[#2B4C6F] hover:bg-[#203A55] text-white font-bold border-2 border-black shadow-[3px_3px_0px_#000] active:translate-y-0.5 active:shadow-none transition-all"
              data-testid="browse-workers-btn">
              Browse Workers
            </Button>
          </div>
        ) : (
          <div className="space-y-4" data-testid="favorites-list">
            <p className="text-sm font-bold text-neutral-500 dark:text-neutral-400 uppercase">{favorites.length} Favorite{favorites.length !== 1 ? 's' : ''}</p>
            {favorites.map(f => (
              <div key={f.id} className="bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700 rounded-xl overflow-hidden shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#2B4C6F]" data-testid={`favorite-card-${f.worker_id}`}>
                <div className="p-5">
                  <div className="flex items-start gap-4">
                    {/* Avatar */}
                    <div className="flex-shrink-0">
                      {f.profile_image ? (
                        <img src={f.profile_image} alt={f.name} className="w-14 h-14 rounded-lg border-2 border-black object-cover" />
                      ) : (
                        <div className="w-14 h-14 rounded-lg border-2 border-black bg-[#2B4C6F] flex items-center justify-center">
                          <UserCircle size={32} weight="bold" className="text-white" />
                        </div>
                      )}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <h3 className="text-lg font-black text-neutral-900 dark:text-white truncate">{f.name}</h3>
                        <div className="flex items-center gap-1.5 ml-3 flex-shrink-0">
                          <button onClick={() => navigate(`/messages?to=${f.worker_id}`)}
                            className="w-9 h-9 bg-[#2B4C6F]/10 hover:bg-[#2B4C6F]/20 rounded-lg flex items-center justify-center border border-[#2B4C6F]/30 transition-colors"
                            title="Message" data-testid={`message-worker-${f.worker_id}`}>
                            <ChatCircle size={16} weight="bold" className="text-[#2B4C6F]" />
                          </button>
                          <button onClick={() => handleRemove(f.worker_id, f.name)}
                            className="w-9 h-9 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/40 rounded-lg flex items-center justify-center border border-red-200 dark:border-red-800 transition-colors"
                            title="Remove" data-testid={`remove-favorite-${f.worker_id}`}>
                            <Trash size={16} weight="bold" className="text-red-500" />
                          </button>
                        </div>
                      </div>

                      <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-neutral-500 dark:text-neutral-400">
                        {f.location && <span className="flex items-center gap-1"><MapPin size={12} weight="bold" /> {f.location}</span>}
                        {f.hourly_rate && <span className="font-bold text-emerald-600">{f.hourly_rate}</span>}
                        <span className="flex items-center gap-1">
                          <Star size={12} weight="fill" className="text-amber-500" /> {f.rating?.toFixed(1)} ({f.reviews_count} reviews)
                        </span>
                        <span className="flex items-center gap-1"><Briefcase size={12} weight="bold" /> {f.jobs_together} job{f.jobs_together !== 1 ? 's' : ''} together</span>
                      </div>

                      {/* Skills */}
                      {f.skills?.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          {f.skills.slice(0, 6).map(s => (
                            <span key={s} className="px-2 py-0.5 bg-neutral-100 dark:bg-neutral-700 text-neutral-600 dark:text-neutral-300 text-xs font-bold rounded border border-neutral-200 dark:border-neutral-600">
                              {s}
                            </span>
                          ))}
                          {f.skills.length > 6 && <span className="text-xs text-neutral-400">+{f.skills.length - 6} more</span>}
                        </div>
                      )}

                      {/* Contact */}
                      <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-neutral-500 dark:text-neutral-400">
                        {f.email && <span className="flex items-center gap-1"><Envelope size={12} weight="bold" /> {f.email}</span>}
                        {f.phone && <span className="flex items-center gap-1"><Phone size={12} weight="bold" /> {f.phone}</span>}
                      </div>

                      {/* Note */}
                      <div className="mt-3">
                        {editingNote === f.worker_id ? (
                          <div className="flex gap-2">
                            <input value={noteValue} onChange={e => setNoteValue(e.target.value)}
                              className="flex-1 h-9 px-3 text-xs bg-white dark:bg-neutral-900 border-2 border-[#2B4C6F] rounded-lg text-neutral-900 dark:text-white"
                              placeholder="Add a private note about this worker..." autoFocus data-testid={`note-input-${f.worker_id}`} />
                            <button onClick={() => handleSaveNote(f.worker_id)}
                              className="h-9 px-3 bg-emerald-600 text-white text-xs font-bold rounded-lg border-2 border-black flex items-center gap-1"
                              data-testid={`save-note-${f.worker_id}`}>
                              <Check size={12} weight="bold" /> Save
                            </button>
                            <button onClick={() => setEditingNote(null)}
                              className="h-9 px-2 bg-neutral-200 dark:bg-neutral-700 text-neutral-700 dark:text-neutral-300 rounded-lg border border-neutral-300 dark:border-neutral-600">
                              <X size={14} weight="bold" />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => { setEditingNote(f.worker_id); setNoteValue(f.note || ''); }}
                            className="text-xs text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 flex items-center gap-1 transition-colors"
                            data-testid={`edit-note-${f.worker_id}`}>
                            <PencilSimple size={12} weight="bold" />
                            {f.note ? <span className="italic">"{f.note}"</span> : 'Add a note...'}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FavoriteWorkers;
